import React, { useEffect, useState } from 'react';
import {
    Alert, Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle,
    FormControlLabel, Switch, TextField, Typography
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import httpClient, { extractErrorMessage } from '../Services/httpClient';

// Compacto: motivos como chips clicáveis (clique abre edição), "Novo motivo" abre o mesmo dialog
// vazio. Code é o identificador estável usado pelo grant (CreateCoinGrantRequest.reasonCode),
// por isso fica travado depois de criado.
const CoinReasonsManager = ({ onReasonsChange }) => {
    const [reasons, setReasons] = useState([]);
    const [error, setError] = useState(null);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editing, setEditing] = useState(null);
    const [code, setCode] = useState('');
    const [label, setLabel] = useState('');
    const [active, setActive] = useState(true);
    const [isSaving, setIsSaving] = useState(false);

    const load = async () => {
        setError(null);
        try {
            const response = await httpClient.get('/backoffice/coin-reasons', { params: { includeInactive: true } });
            setReasons(response.data);
            onReasonsChange?.(response.data);
        } catch (err) {
            setError(extractErrorMessage(err));
        }
    };

    useEffect(() => {
        load();
    }, []);

    const openNew = () => {
        setEditing(null);
        setCode('');
        setLabel('');
        setActive(true);
        setDialogOpen(true);
    };

    const openEdit = (reason) => {
        setEditing(reason);
        setCode(reason.code);
        setLabel(reason.label);
        setActive(reason.active);
        setDialogOpen(true);
    };

    const handleSave = async () => {
        setIsSaving(true);
        setError(null);
        try {
            await httpClient.post('/backoffice/coin-reasons', { code, label, active });
            setDialogOpen(false);
            await load();
        } catch (err) {
            setError(extractErrorMessage(err));
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <Typography variant="body2" sx={{ fontWeight: 'bold', color: 'text.secondary' }}>
                    Motivos
                </Typography>
                <Button size="small" startIcon={<AddIcon fontSize="small" />} onClick={openNew}>
                    Novo motivo
                </Button>
            </Box>

            {error && <Alert severity="error" sx={{ mb: 1 }}>{error}</Alert>}

            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {reasons.length === 0 && (
                    <Typography variant="body2" color="text.secondary">Nenhum motivo cadastrado.</Typography>
                )}
                {reasons.map((reason) => (
                    <Chip
                        key={reason.id}
                        label={reason.label}
                        onClick={() => openEdit(reason)}
                        variant={reason.active ? 'filled' : 'outlined'}
                        style={reason.active ? { backgroundColor: '#fff4ec', color: '#f77513' } : { color: '#999' }}
                    />
                ))}
            </Box>

            <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} fullWidth maxWidth="xs">
                <DialogTitle>{editing ? 'Editar motivo' : 'Novo motivo'}</DialogTitle>
                <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
                    <TextField
                        label="Código"
                        size="small"
                        value={code}
                        onChange={(e) => setCode(e.target.value)}
                        disabled={Boolean(editing)}
                        helperText={editing ? 'Não pode ser alterado' : 'Identificador único, ex: BONUS_LANCAMENTO'}
                    />
                    <TextField label="Rótulo" size="small" value={label} onChange={(e) => setLabel(e.target.value)} />
                    <FormControlLabel
                        control={<Switch checked={active} onChange={(e) => setActive(e.target.checked)} />}
                        label="Ativo"
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDialogOpen(false)} disabled={isSaving} style={{ color: '#666' }}>Cancelar</Button>
                    <Button
                        variant="contained"
                        disabled={isSaving || !code.trim() || !label.trim()}
                        onClick={handleSave}
                        style={{ backgroundColor: '#f77513', color: 'white' }}
                    >
                        Salvar
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default CoinReasonsManager;
